<script setup>
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import ImageResize from 'tiptap-extension-resize-image';
import Mention from '@tiptap/extension-mention';
import Link from '@tiptap/extension-link';

const props = defineProps({
  modelValue: String,
  placeholder: String,
  size: {
    type: String,
    default: 'md'
  },
  label: String,
  minHeight: {
    type: String,
    default: 'min-h-[100px]'
  },
  rows: {
    type: Number,
    default: null
  },
  showImageUpload: {
    type: Boolean,
    default: false
  },
  profiles: {
    type: Array,
    default: () => []
  },
  setTaggedPeople: {
    type: Function,
    default: () => { }
  },
  showAttachmentButton: {
    type: Boolean,
    default: false
  },
});

const emit = defineEmits(['update:modelValue', 'attachment-upload']);
const fileInput = ref(null);

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Helper to extract mentions from editor content
function extractMentionsFromHTML(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  const mentionNodes = div.querySelectorAll('span.tiptap-mention');
  const tags = [];
  mentionNodes.forEach(node => {
    const name = node.textContent.replace(/^@/, '');
    tags.push({ name });
  });
  return tags;
}

const renderSuggestion = {
  items: ({ query }) => {
    if (!query) {
      return props.profiles;
    }
    return props.profiles.filter(profile => {
      const name = profile.name || '';
      return name.toLowerCase().includes(query.toLowerCase());
    });
  },
  render: () => {
    let component, popup;
    let selectedIndex = 0;
    return {
      onStart: (props) => {
        component = document.createElement('div');
        component.className = 'tiptap-mention-dropdown bg-white border rounded shadow-lg z-50';
        component.style.position = 'fixed';
        component.style.minWidth = '200px';
        // Find the editor's parent container
        const editorEl = document.querySelector('.editor-content');
        const parentEl = editorEl ? editorEl.parentElement : document.body;
        parentEl.appendChild(component);
        popup = document.createElement('ul');
        popup.className = 'list-none m-0 p-0';
        component.appendChild(popup);
        selectedIndex = 0;
        updatePosition(props);
        renderItems(props);
      },
      onUpdate(props) {
        if (selectedIndex >= props.items.length) selectedIndex = 0;
        updatePosition(props);
        renderItems(props);
      },
      onKeyDown(props) {
        if (props.event.key === 'Escape') {
          component.remove();
          return true;
        }
        return false;
      },
      onExit() {
        component.remove();
      }
    };
    function updatePosition(props) {
      const rect = props.clientRect?.();
      const editorEl = document.querySelector('.editor-content');
      const parentEl = editorEl ? editorEl.parentElement : document.body;
      if (rect && parentEl) {
        // Get parent container's bounding rect
        const parentRect = parentEl.getBoundingClientRect();
        component.style.position = 'absolute';
        component.style.left = (rect.left - parentRect.left) + 'px';
        component.style.top = (rect.bottom - parentRect.top) + 'px';
        component.style.zIndex = '99999';
        component.style.pointerEvents = 'auto';
      }
    }
    function renderItems({ items, command }) {
      popup.innerHTML = '';
      items.forEach((item, idx) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'w-full text-left px-4 py-2 focus:outline-none';
        btn.style.cursor = 'pointer';
        if (idx === selectedIndex) {
          btn.className += ' bg-blue-100 text-blue-800';
        }
        btn.textContent = item.name || 'Unknown';
        btn.onmouseover = () => {
          selectedIndex = idx;
          Array.from(popup.children).forEach((child, i) => {
            if (i === selectedIndex) {
              child.classList.add('bg-blue-100', 'text-blue-800');
            } else {
              child.classList.remove('bg-blue-100', 'text-blue-800');
            }
          });
        };
        btn.onmousedown = (e) => {
          e.preventDefault(); // Prevent editor blur
          command({
            label: item.name || 'Unknown',
          });
        };
        popup.appendChild(btn);
      });
      if (!items.length) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'w-full text-left px-4 py-2 text-gray-400 cursor-default';
        btn.textContent = 'No users found';
        btn.disabled = true;
        popup.appendChild(btn);
      }
    }
  }
};

const editor = useEditor({
  content: props.modelValue || '',
  extensions: [
    StarterKit,
    Placeholder.configure({
      placeholder: props.placeholder || 'Start writing...',
    }),
    Image.configure({
      allowBase64: true,
      HTMLAttributes: {
        class: 'max-w-full h-auto',
      },
    }),
    ImageResize,
    Mention.configure({
      HTMLAttributes: {
        class: 'tiptap-mention bg-blue-100 text-blue-800 rounded px-1',
      },
      suggestion: renderSuggestion,
    }),
    Link.configure({
      openOnClick: false,
      autolink: true,
      protocols: ['http', 'https', 'mailto', 'tel'],
      HTMLAttributes: {
        class: 'text-primary-600 hover:text-primary-800 underline cursor-pointer',
      },
    }),
  ],
  editorProps: {
    attributes: {
      class: 'prose prose-sm focus:outline-none w-full ' + getHeightClasses() + ' ' + getPaddingClasses(),
    },
    handlePaste: (view, event) => {
      const items = event.clipboardData?.items;
      if (!items) return false;

      for (const item of items) {
        if (item.type.indexOf('image') === 0) {
          event.preventDefault();
          const file = item.getAsFile();
          handleImageUpload(file)
            .then(imageData => {
              editor.value?.chain().focus().setImage(imageData).run();
            })
            .catch(error => {
              console.error('Image paste failed:', error);
            });
          return true;
        }
      }
      return false;
    },
    handleDrop: (view, event) => {
      // Handle pasted content to preserve links
      const html = event.dataTransfer?.getData('text/html');
      if (html) {
        // Let the default handler process it, which will preserve links
        return false;
      }
      return false;
    },
    handleClick: (view, event) => {
      // Handle Ctrl+click on links
      if (event.ctrlKey || event.metaKey) {
        const linkElement = event.target.closest('a');
        if (linkElement && linkElement.href) {
          event.preventDefault();
          event.stopPropagation();
          window.open(linkElement.href, '_blank');
          return true;
        }
      }
      return false;
    },
  },
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML());
  },
});

async function handleImageUpload(file) {
  if (!file) {
    throw new Error('No file provided');
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File size exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit`);
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve({
        src: e.target.result,
        title: file.name,
        loading: 'lazy'
      });
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

async function handleFileChange(event) {
  const file = event.target.files[0];
  if (!file) return;

  try {
    const imageData = await handleImageUpload(file);
    editor.value?.chain().focus().setImage(imageData).run();
  } catch (error) {
    console.error('Image upload failed:', error);
    // You might want to show a toast or notification here
  }
}

function handleAttachmentClick() {
  emit('attachment-upload');
}

// Watch for external modelValue changes
watch(() => props.modelValue, (newValue) => {
  const isSame = newValue === editor.value?.getHTML();
  if (editor.value && !isSame) {
    editor.value.commands.setContent(newValue || '');
  }
});

watch(() => editor.value?.getHTML(), (newHTML) => {
  if (props.profiles.length > 0) {
    const taggedPeople = extractMentionsFromHTML(newHTML);
    props.setTaggedPeople(taggedPeople);
  }
});

// Add click handler for links after editor is mounted
onMounted(() => {
  if (editor.value) {
    const editorElement = editor.value.view.dom;
    editorElement.addEventListener('click', (event) => {
      if (event.ctrlKey || event.metaKey) {
        const linkElement = event.target.closest('a');
        if (linkElement && linkElement.href) {
          event.preventDefault();
          event.stopPropagation();
          window.open(linkElement.href, '_blank');
        }
      }
    });
  }
});

function getSizeClasses() {
  switch (props.size) {
    case 'xs':
      return 'text-xs';
    case 'sm':
      return 'text-sm';
    case 'md':
      return ''; // Default styling
    case 'lg':
      return 'text-lg';
    case 'xl':
      return 'text-xl';
    default:
      return '';
  }
}

function getPaddingClasses() {
  switch (props.size) {
    case 'xs':
      return 'p-2';
    case 'sm':
      return 'p-3';
    case 'md':
      return 'p-4';
    case 'lg':
      return 'p-5';
    case 'xl':
      return 'p-6';
    default:
      return 'p-4';
  }
}

function getToolbarPaddingClasses() {
  switch (props.size) {
    case 'xs':
      return 'p-1';
    case 'sm':
      return 'p-1.5';
    case 'md':
      return 'p-2';
    case 'lg':
      return 'p-3';
    case 'xl':
      return 'p-4';
    default:
      return 'p-2';
  }
}

function getButtonSize() {
  switch (props.size) {
    case 'xs':
      return 'xs';
    case 'sm':
      return 'sm';
    case 'md':
      return 'md';
    case 'lg':
      return 'lg';
    case 'xl':
      return 'xl';
    default:
      return 'md';
  }
}

function getHeightClasses() {
  if (props.rows) {
    // Calculate height based on rows and size
    const lineHeight = props.size === 'xs' ? 1.125 : props.size === 'sm' ? 1.25 : props.size === 'lg' ? 1.75 : props.size === 'xl' ? 2 : 1.5;
    const padding = props.size === 'xs' ? 8 : props.size === 'sm' ? 12 : props.size === 'lg' ? 20 : props.size === 'xl' ? 24 : 16;
    const height = Math.round((props.rows * lineHeight * 16) + (padding * 2)); // 16px base font size
    return `h-[${height}px]`;
  }
  return props.minHeight;
}

onBeforeUnmount(() => {
  if (editor.value) {
    editor.value.destroy();
  }
});
</script>

<template>
  <div class="flex flex-col gap-1.5 h-full">
    <div
      class="border border-gray-300 rounded-lg bg-white flex flex-col focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/50 h-full"
      :class="getSizeClasses()">
      <!-- Toolbar -->
      <div v-if="editor"
        :class="getToolbarPaddingClasses() + ' border-b border-gray-200 flex flex-wrap gap-3 bg-gray-50 rounded-t-lg'">
        <!-- Undo -->
        <Tooltip text="Undo">
          <UButton @click="editor.chain().focus().undo().run()" :disabled="!editor.can().undo()" :variant="'ghost'"
            color="primary" icon="i-material-symbols-undo-rounded" square :size="getButtonSize()" />
        </Tooltip>

        <!-- Redo -->
        <Tooltip text="Redo">
          <UButton @click="editor.chain().focus().redo().run()" :disabled="!editor.can().redo()" :variant="'ghost'"
            color="primary" icon="i-material-symbols-redo-rounded" square :size="getButtonSize()" />
        </Tooltip>

        <!-- Divider -->
        <div class="w-px h-7 flex my-auto bg-gray-200 mx-1" />

        <!-- Bold -->
        <Tooltip text="Bold">
          <UButton @click="editor.chain().focus().toggleBold().run()"
            :disabled="!editor.can().chain().focus().toggleBold().run()"
            :variant="editor.isActive('bold') ? 'solid' : 'ghost'" color="primary"
            icon="i-material-symbols-format-bold-rounded" square :size="getButtonSize()" />
        </Tooltip>

        <!-- Italic -->
        <Tooltip text="Italic">
          <UButton @click="editor.chain().focus().toggleItalic().run()"
            :disabled="!editor.can().chain().focus().toggleItalic().run()"
            :variant="editor.isActive('italic') ? 'solid' : 'ghost'" color="primary"
            icon="i-material-symbols-format-italic-rounded" square :size="getButtonSize()" />
        </Tooltip>

        <!-- Strike -->
        <Tooltip text="Strike">
          <UButton @click="editor.chain().focus().toggleStrike().run()"
            :disabled="!editor.can().chain().focus().toggleStrike().run()"
            :variant="editor.isActive('strike') ? 'solid' : 'ghost'" color="primary"
            icon="i-material-symbols-strikethrough-s-rounded" square :size="getButtonSize()" />
        </Tooltip>

        <!-- Bullet List -->
        <Tooltip text="Bullet List">
          <UButton @click="editor.chain().focus().toggleBulletList().run()"
            :variant="editor.isActive('bulletList') ? 'solid' : 'ghost'" color="primary"
            icon="i-material-symbols-format-list-bulleted-rounded" square :size="getButtonSize()" />
        </Tooltip>

        <!-- Ordered List -->
        <Tooltip text="Ordered List">
          <UButton @click="editor.chain().focus().toggleOrderedList().run()"
            :variant="editor.isActive('orderedList') ? 'solid' : 'ghost'" color="primary"
            icon="i-material-symbols-format-list-numbered-rounded" square :size="getButtonSize()" />
        </Tooltip>

        <template v-if="showImageUpload">
          <!-- Divider -->
          <div class="w-px h-7 flex my-auto bg-gray-200 mx-1" />

          <!-- Image Upload -->
          <Tooltip text="Upload Image">
            <UButton @click="fileInput.click()" variant="ghost" color="primary" icon="i-material-symbols-image-rounded"
              square :size="getButtonSize()" />
          </Tooltip>

          <input ref="fileInput" type="file" accept="image/*" class="hidden" @change="handleFileChange" />
        </template>

        <template v-if="showAttachmentButton">
          <!-- Divider -->
          <div class="w-px h-7 flex my-auto bg-gray-200 mx-1" />

          <!-- Attachment Upload -->
          <Tooltip text="Add Attachment">
            <UButton @click="handleAttachmentClick" variant="ghost" color="primary"
              icon="i-material-symbols-attach-file-rounded" square :size="getButtonSize()" />
          </Tooltip>
        </template>
      </div>

      <!-- Editor -->
      <div class="editor-wrapper flex-1 min-h-0">
        <EditorContent :editor="editor" class="editor-content h-full" />
      </div>
    </div>
    <span v-if="label" class="text-sm text-gray-500">{{ label }}</span>
  </div>
</template>

<style>
/* Global styles needed for Tiptap to work properly */
.ProseMirror {
  outline: none;
  height: 100%;
  overflow-y: auto;
}

/* Image resize styles */
.resize-image {
  position: relative;
  display: inline-block;
  max-width: 100%;
  height: auto;
}

.resize-image img {
  max-width: 100%;
  height: auto;
  cursor: move;
}

.resize-image::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px solid transparent;
  transition: border-color 0.2s;
}

.resize-image:hover::after {
  border-color: #4f46e5;
}

.resize-image .resize-handle {
  position: absolute;
  width: 10px;
  height: 10px;
  background-color: #4f46e5;
  border: 2px solid white;
  border-radius: 50%;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
}

.resize-image:hover .resize-handle {
  opacity: 1;
}

.resize-image .resize-handle.nw {
  top: -5px;
  left: -5px;
  cursor: nw-resize;
}

.resize-image .resize-handle.ne {
  top: -5px;
  right: -5px;
  cursor: ne-resize;
}

.resize-image .resize-handle.sw {
  bottom: -5px;
  left: -5px;
  cursor: sw-resize;
}

.resize-image .resize-handle.se {
  bottom: -5px;
  right: -5px;
  cursor: se-resize;
}

.ProseMirror ul {
  list-style-type: disc !important;
  padding-left: 1.5em !important;
}

.ProseMirror ol {
  list-style-type: decimal !important;
  padding-left: 1.5em !important;
}

.ProseMirror blockquote {
  border-left: 3px solid #e2e8f0 !important;
  padding-left: 1em !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.ProseMirror p.is-editor-empty:first-child::before {
  color: #9ca3af;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

/* Size variants */
.editor-wrapper {
  position: relative;
}

.editor-content {
  width: 100%;
}
</style>